import { useState, useEffect, useCallback } from 'react'
import { Routes, Route, Navigate } from 'react-router-dom'
import { AuthProvider, useAuth } from './contexts/AuthContext'
import Layout from './components/Layout'
import Login from './pages/Login'
import Dashboard from './pages/Dashboard'
import CattleList from './pages/cattle/CattleList'
import CattleDetail from './pages/cattle/CattleDetail'
import KraalFeed from './pages/cattle/KraalFeed'
import CattleForm from './pages/cattle/CattleForm'
import EventForm from './pages/cattle/EventForm'
import PendingEventsPage from './pages/cattle/PendingEventsPage'
import CensusCapture from './pages/census/CensusCapture'
import PasturePage from './pages/pasture/PasturePage'
import PaddockList from './pages/paddocks/PaddockList'
import PaddockForm from './pages/paddocks/PaddockForm'
import PaddockMap from './pages/paddocks/PaddockMap'
import PaddockDetail from './pages/paddocks/PaddockDetail'
import FinanceDashboard from './pages/finance/FinanceDashboard'
import DirectorAccounts from './pages/finance/DirectorAccounts'
import ExpenseList from './pages/finance/ExpenseList'
import ExpenseForm from './pages/finance/ExpenseForm'
import IncomeForm from './pages/finance/IncomeForm'
import IncomeList from './pages/finance/IncomeList'
import BiocharDashboard from './pages/biochar/BiocharDashboard'
import BiocharList from './pages/biochar/BiocharList'
import BatchForm from './pages/biochar/BatchForm'
import BatchOperatorForm from './pages/biochar/BatchOperatorForm'

import AgentsPage from './pages/agents/AgentsPage'
import AssistantPage from './pages/assistant/AssistantPage'
import UserManagement from './pages/users/UserManagement'
import ApprovalsInbox from './pages/approvals/ApprovalsInbox'
import WeighSessionImport from './pages/cattle/WeighSessionImport'
import PregnancyCampaignImport from './pages/cattle/PregnancyCampaignImport'
import PhotoSessionUpload from './pages/cattle/PhotoSessionUpload'
import GrazingCalendar from './pages/grazing/GrazingCalendar'
import LantanaDashboard from './pages/lantana/LantanaDashboard'
import LantanaPatchForm from './pages/lantana/LantanaPatchForm'
import ClearerFieldView from './pages/lantana/ClearerFieldView'
import RanchSetup from './pages/ranch/RanchSetup'
import RanchSettings from './pages/ranch/RanchSettings'
import PaddockReconfigure from './pages/paddocks/PaddockReconfigure'
import BulkTreatmentForm from './pages/cattle/BulkTreatmentForm'
import ReportList from './pages/reports/ReportList'
import ReportViewer from './pages/reports/ReportViewer'
import SmartKraalSync from './pages/cattle/SmartKraalSync'
import CrushRegistration from './pages/cattle/CrushRegistration'
import EvidenceLedger from './pages/reports/EvidenceLedger'
import WeeklyReport from './pages/reports/WeeklyReport'
import TwinPage from './pages/twin/TwinPage'
import TwinFleet from './pages/twin/TwinFleet'
import MarketPrices from './pages/market/MarketPrices'
import MonitoringPoints from './pages/land/MonitoringPoints'
import MonitoringPointDetail from './pages/land/MonitoringPointDetail'
import BiomassLog from './pages/land/BiomassLog'
import SoilSamples from './pages/land/SoilSamples'
import TrainingData from './pages/land/TrainingData'
import DroneCalibration from './pages/land/DroneCalibration'
import DroneRuns from './pages/land/DroneRuns'
import VegetationPolygons from './pages/land/VegetationPolygons'
import PilotTracker from './pages/land/PilotTracker'
import FieldMarkers from './pages/land/FieldMarkers'
import CycleProgress from './pages/land/CycleProgress'
import InstrumentCalibrations from './pages/land/InstrumentCalibrations'
import FieldLog from './pages/land/FieldLog'
import DairyDashboard from './pages/dairy/DairyDashboard'
import MilkSessionPage from './pages/dairy/MilkSessionPage'
import MilkSessionImport from './pages/dairy/MilkSessionImport'
import MilkQualityPage from './pages/dairy/MilkQualityPage'
import LactationDashboard from './pages/dairy/LactationDashboard'
import ReproductiveCalendar from './pages/dairy/ReproductiveCalendar'
import MastitisLog from './pages/dairy/MastitisLog'
import MilkBuyers from './pages/dairy/MilkBuyers'
import AIModels from './pages/admin/AIModels'
import FleetAssurance from './pages/admin/FleetAssurance'
import InventoryList from './pages/inventory/InventoryList'
import LantanaCoverageReport from './pages/land/LantanaCoverageReport'
import NotFound from './pages/NotFound'

const DEFAULT_RANCH_ID = '00000000-0000-0000-0000-000000000001'

function RequireAuth({ children }) {
  const { isAuthenticated, isLoading } = useAuth()
  // Don't redirect while /auth/me is still in flight — the cookie-based auth
  // flow can't answer synchronously. Without this guard, a fresh page load
  // flashes to /login before /me resolves.
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-earth-50">
        <div className="animate-spin rounded-full h-8 w-8 border-2 border-primary-600 border-t-transparent" />
      </div>
    )
  }
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />
  }
  return children
}

// Role-aware landing for the index route. Owners land on the Assistant —
// their home is "ask and read", not the operational dashboard (which stays
// reachable from the nav). Narrow roles bounce again from /dashboard via
// NARROW_ROLE_LANDING; everyone else starts on the dashboard.
function RoleLanding() {
  const { role, isLoading } = useAuth()
  if (isLoading) return null
  return <Navigate to={role === 'owner' ? '/assistant' : '/dashboard'} replace />
}

function AppRoutes({ currentRanchId, handleRanchChange }) {
  return (
    <Routes>
      <Route path="/login" element={<Login />} />

      <Route path="/" element={
        <RequireAuth>
          <Layout currentRanchId={currentRanchId} />
        </RequireAuth>
      }>
        <Route index element={<RoleLanding />} />
        <Route path="dashboard" element={<Dashboard ranchId={currentRanchId} />} />

      {/* Cattle */}
      <Route path="cattle" element={<CattleList ranchId={currentRanchId} />} />
      <Route path="cattle/new" element={<CattleForm ranchId={currentRanchId} />} />
      {/* The kraal feed: who the kraal saw, day by day (plan smartkraal-owner-experience B2) */}
      <Route path="cattle/feed" element={<KraalFeed ranchId={currentRanchId} />} />
      <Route path="cattle/:id" element={<CattleDetail ranchId={currentRanchId} />} />
      <Route path="cattle/:id/edit" element={<CattleForm ranchId={currentRanchId} />} />
      <Route path="cattle/:animalId/event" element={<EventForm ranchId={currentRanchId} />} />
      <Route path="cattle/events/new" element={<EventForm ranchId={currentRanchId} />} />
      <Route path="cattle/weigh-session" element={<WeighSessionImport ranchId={currentRanchId} />} />
      <Route path="cattle/smartkraal" element={<SmartKraalSync ranchId={currentRanchId} />} />
      <Route path="cattle/registration" element={<CrushRegistration ranchId={currentRanchId} />} />
      <Route path="cattle/preg-campaign" element={<PregnancyCampaignImport ranchId={currentRanchId} />} />
      <Route path="cattle/photo-upload" element={<PhotoSessionUpload ranchId={currentRanchId} />} />
      <Route path="cattle/bulk-treatment" element={<BulkTreatmentForm ranchId={currentRanchId} />} />
      <Route path="cattle/events/pending" element={<PendingEventsPage ranchId={currentRanchId} />} />
      <Route path="census" element={<CensusCapture ranchId={currentRanchId} />} />
      {/* Pasture pins — the herdsman capture surface (plan pasture-pin-protocol Phase 2) */}
      <Route path="pasture" element={<PasturePage ranchId={currentRanchId} />} />
      <Route path="events/new" element={<EventForm ranchId={currentRanchId} />} />

      {/* Ranch Settings */}
      <Route path="ranch/setup" element={<RanchSetup />} />
      <Route path="ranch/settings" element={
        <RanchSettings
          currentRanchId={currentRanchId}
          onRanchChange={handleRanchChange}
        />
      } />

      {/* Paddocks */}
      <Route path="paddocks" element={<PaddockList ranchId={currentRanchId} />} />
      <Route path="paddocks/new" element={<PaddockForm ranchId={currentRanchId} />} />
      <Route path="paddocks/map" element={<PaddockMap ranchId={currentRanchId} />} />
      <Route path="paddocks/reconfigure" element={<PaddockReconfigure ranchId={currentRanchId} />} />
      <Route path="paddocks/:paddockId" element={<PaddockDetail ranchId={currentRanchId} />} />

      {/* Grazing Calendar */}
      <Route path="grazing" element={<GrazingCalendar ranchId={currentRanchId} />} />

      {/* Finance */}
      <Route path="finance" element={<FinanceDashboard ranchId={currentRanchId} />} />
      <Route path="finance/director-accounts" element={<DirectorAccounts ranchId={currentRanchId} />} />
      <Route path="finance/expenses/new" element={<ExpenseForm ranchId={currentRanchId} />} />
      <Route path="expenses" element={<ExpenseList ranchId={currentRanchId} />} />
      <Route path="expenses/new" element={<ExpenseForm ranchId={currentRanchId} />} />
      <Route path="income" element={<IncomeList ranchId={currentRanchId} />} />
      <Route path="income/new" element={<IncomeForm ranchId={currentRanchId} />} />

      {/* Biochar */}
      <Route path="biochar" element={<BiocharDashboard ranchId={currentRanchId} />} />
      <Route path="biochar/batches" element={<BiocharList ranchId={currentRanchId} />} />
      <Route path="biochar/batches/new" element={<BatchForm ranchId={currentRanchId} />} />
      <Route path="biochar/new" element={<BatchForm ranchId={currentRanchId} />} />
      <Route path="biochar/batches/:batchId" element={<BatchOperatorForm ranchId={currentRanchId} />} />
      <Route path="biochar/batches/:batchId/process" element={<BatchOperatorForm ranchId={currentRanchId} />} />

      {/* Lantana Management */}
      <Route path="lantana" element={<LantanaDashboard ranchId={currentRanchId} />} />
      <Route path="lantana/new" element={<LantanaPatchForm ranchId={currentRanchId} />} />
      <Route path="lantana/field" element={<ClearerFieldView ranchId={currentRanchId} />} />
      <Route path="lantana/:patchId" element={<LantanaPatchForm ranchId={currentRanchId} />} />
      <Route path="lantana/:patchId/edit" element={<LantanaPatchForm ranchId={currentRanchId} />} />

      {/* Narrow-role landing pages (Session 2 of roles overhaul) */}
      <Route path="my-lantana" element={<ClearerFieldView ranchId={currentRanchId} />} />
      <Route path="my-biochar" element={<BiocharList ranchId={currentRanchId} />} />

      {/* Dairy */}
      <Route path="dairy" element={<DairyDashboard ranchId={currentRanchId} />} />
      <Route path="dairy/record-session" element={<MilkSessionPage ranchId={currentRanchId} />} />
      <Route path="dairy/import-csv" element={<MilkSessionImport ranchId={currentRanchId} />} />
      <Route path="dairy/quality" element={<MilkQualityPage ranchId={currentRanchId} />} />
      <Route path="dairy/lactation" element={<LactationDashboard ranchId={currentRanchId} />} />
      <Route path="dairy/reproductive" element={<ReproductiveCalendar ranchId={currentRanchId} />} />
      <Route path="dairy/mastitis" element={<MastitisLog ranchId={currentRanchId} />} />
      <Route path="dairy/buyers" element={<MilkBuyers ranchId={currentRanchId} />} />

      {/* Market Prices */}
      <Route path="market-prices" element={<MarketPrices ranchId={currentRanchId} />} />

      {/* Land Monitoring */}
      <Route path="land" element={<MonitoringPoints ranchId={currentRanchId} />} />
      <Route path="land/biomass" element={<BiomassLog ranchId={currentRanchId} />} />
      <Route path="land/soil-samples" element={<SoilSamples ranchId={currentRanchId} />} />
      <Route path="land/training-data" element={<TrainingData ranchId={currentRanchId} />} />
      <Route path="land/drone-calibration" element={<DroneCalibration ranchId={currentRanchId} />} />
      <Route path="land/drone-runs" element={<DroneRuns ranchId={currentRanchId} />} />
      <Route path="land/polygons" element={<VegetationPolygons ranchId={currentRanchId} />} />
      <Route path="land/pilot" element={<PilotTracker ranchId={currentRanchId} />} />
      <Route path="land/markers" element={<FieldMarkers ranchId={currentRanchId} />} />
      <Route path="land/cycle-progress" element={<CycleProgress ranchId={currentRanchId} />} />
      <Route path="land/instruments" element={<InstrumentCalibrations ranchId={currentRanchId} />} />
      <Route path="land/field-log" element={<FieldLog ranchId={currentRanchId} />} />
      <Route path="land/:pointId" element={<MonitoringPointDetail ranchId={currentRanchId} />} />

      {/* Reports */}
      <Route path="reports" element={<ReportList ranchId={currentRanchId} />} />
      <Route path="reports/weekly" element={<WeeklyReport ranchId={currentRanchId} />} />
      <Route path="twin" element={<TwinPage ranchId={currentRanchId} />} />
      {/* Twin Fleet — cross-farm twin worklist (plan Phase 6); membership-gated server-side */}
      <Route path="twin/fleet" element={<TwinFleet />} />
      <Route path="reports/evidence-ledger" element={<EvidenceLedger ranchId={currentRanchId} />} />
      <Route path="reports/:reportId" element={<ReportViewer ranchId={currentRanchId} />} />

      {/* Assistant (in-app owner agent — Q&A chat + digest viewing) */}
      <Route path="assistant" element={<AssistantPage ranchId={currentRanchId} />} />

      {/* Agents */}
      <Route path="agents" element={<AgentsPage ranchId={currentRanchId} />} />
      <Route path="agents/discrepancies" element={<AgentsPage ranchId={currentRanchId} />} />

      {/* Users */}
      <Route path="users" element={<UserManagement ranchId={currentRanchId} />} />

      {/* Approvals Inbox (Session 3 of roles-permissions overhaul) */}
      <Route path="approvals" element={<ApprovalsInbox ranchId={currentRanchId} />} />

      {/* AI Model Registry (S3 of biochar pilot land monitoring, mig 098) */}
      <Route path="ai-models" element={<AIModels />} />

      {/* Fleet Assurance — sysadmin-only cross-farm trust-score worklist (mig 109) */}
      <Route path="fleet-assurance" element={<FleetAssurance />} />

      {/* Drug/vaccine inventory — stock, counts, mass-balance (mig 110) */}
      <Route path="inventory" element={<InventoryList ranchId={currentRanchId} />} />

      {/* Lantana coverage report (S3 of biochar pilot land monitoring, mig 099) */}
      <Route
        path="land/lantana-coverage"
        element={<LantanaCoverageReport ranchId={currentRanchId} />}
      />

      {/* 404 Catch-all */}
      <Route path="*" element={<NotFound />} />
    </Route>
    </Routes>
  )
}

// Once identity (and the user's ranch memberships) load, make sure the active
// ranch is one they can actually access. A fresh browser has no
// `currentRanchId` in localStorage and falls back to DEFAULT_RANCH_ID (the demo
// ranch), which a real single-ranch member is NOT part of — so every data
// request 403s ("No access to this ranch"). Auto-select their first ranch and
// hold the spinner until it's applied, so no route renders against a ranch the
// user is forbidden from (which would flash 403 errors across the dashboard).
//
// System admins have no ranch_users rows (ranches is empty); they keep the
// current selection and reach any ranch via the cross-tenant bypass.
function RanchBoundary({ currentRanchId, onSelect, children }) {
  const { ranches, isAuthenticated } = useAuth()
  const needsSelection =
    isAuthenticated &&
    Array.isArray(ranches) &&
    ranches.length > 0 &&
    !ranches.some((r) => r.id === currentRanchId)

  useEffect(() => {
    if (needsSelection) onSelect(ranches[0].id)
  }, [needsSelection, ranches, onSelect])

  if (needsSelection) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-earth-50">
        <div className="animate-spin rounded-full h-8 w-8 border-2 border-primary-600 border-t-transparent" />
      </div>
    )
  }
  return children
}

function App() {
  // Current ranch state (allows switching between ranches)
  const [currentRanchId, setCurrentRanchId] = useState(
    localStorage.getItem('currentRanchId') || DEFAULT_RANCH_ID
  )

  // Handle ranch switching
  const handleRanchChange = useCallback((newRanchId) => {
    setCurrentRanchId(newRanchId)
    localStorage.setItem('currentRanchId', newRanchId)
  }, [])

  return (
    <AuthProvider ranchId={currentRanchId}>
      <RanchBoundary currentRanchId={currentRanchId} onSelect={handleRanchChange}>
        <AppRoutes currentRanchId={currentRanchId} handleRanchChange={handleRanchChange} />
      </RanchBoundary>
    </AuthProvider>
  )
}

export default App
